import {
  useConnectFacebookToProxima,
  useFacebook,
  useHandleApi,
  useIntegrations,
  useKlaviyo,
  useShopify,
  useShowSpinner,
} from 'hooks';
import { AgentResponse, Message, VALID_INTEGRATION_TYPES, ValidIntegrationType } from './types';
import { useEffect, useState } from 'react';
import { useBrandStore, useIntegrationsStore } from 'stores';
import { debounce } from 'lodash';
import { BrandIntegrationStatus, IntegrationType } from 'constants/integrations';
import { Integration } from 'types/stores/integration-store';
import * as datefns from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShopifyAppRedirect } from 'types/components/shopify';
import MetaIcon from './Layout/icons/Meta';
import ShopifyIcon from './Layout/icons/Shopify';
import KlaviyoIcon from './Layout/icons/Klaviyo';

const debouncer = debounce((fn) => fn(), 800);

const IntegrationMappings = {
  [IntegrationType.Facebook]: 'Meta',
  [IntegrationType.Shopify]: 'Shopify',
  [IntegrationType.Klaviyo]: 'Klaviyo',
};

const Icons = {
  [IntegrationType.Facebook]: MetaIcon,
  [IntegrationType.Shopify]: ShopifyIcon,
  [IntegrationType.Klaviyo]: KlaviyoIcon,
};

const useAIAgent = () => {
  const apiUrl = import.meta.env.VITE_AGENT_API_URL;
  const { integrations } = useIntegrationsStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const { handleApi, loading: handleApiLoading } = useHandleApi({ apiDomain: apiUrl });
  const { brand } = useBrandStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { onClick: onMetaConnect } = useConnectFacebookToProxima({
    redirect: pathname,
    brandId: brand.brandId,
  });
  const { loading: integrationsLoading, initializeIntegrations } = useIntegrations();
  const { setShopifyURLRedirect, getShopifyStatus } = useShopify();
  const { getMetaStatus, onAcceptTermsClick, onConnectAdAccountClick } = useFacebook();
  const { getKlaviyoStatus } = useKlaviyo();

  const loading = handleApiLoading || integrationsLoading;

  useShowSpinner({ show: loading });

  const onSendMessage = (message: string) => {
    if (!message.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      content: message,
      timestamp: new Date().toISOString(),
      sender: 'user',
    };

    setMessages((prevMessages) => [...prevMessages, newMessage]);

    debouncer(async () => {
      const response = await handleApi({
        endpoint: `/api/brands/${brand.brandId}/agent`,
        method: 'POST',
        data: {
          question: message.trim(),
        },
      });

      const { messageContent, vizBase64 } = response.data as AgentResponse;

      const newResponse: Message = {
        id: Date.now().toString(),
        content: messageContent,
        vizBase64,
        timestamp: new Date().toISOString(),
        responseOf: newMessage, // not sure about this
        sender: 'agent',
      };

      setMessages((prevMessages) => [...prevMessages, newResponse]);
    });
  };

  const onFeedbackClick = (messageId: string, feedback: 'up' | 'down') => {
    const updatedMessages = messages.map((msg) => {
      if (msg.id === messageId) {
        const currentFeedback = msg.feedback || { upvote: false, downvote: false };
        return {
          ...msg,
          feedback: {
            ...currentFeedback,
            [feedback === 'up' ? 'upvote' : 'downvote']:
              !currentFeedback[feedback === 'up' ? 'upvote' : 'downvote'],
          },
        };
      }
      return msg;
    });

    setMessages(updatedMessages);
  };

  const getIntegrationStatus = (type: ValidIntegrationType): BrandIntegrationStatus => {
    const map: Record<ValidIntegrationType, BrandIntegrationStatus> = {
      [IntegrationType.Facebook]: getMetaStatus(),
      [IntegrationType.Shopify]: getShopifyStatus(),
      [IntegrationType.Klaviyo]: getKlaviyoStatus(),
    };

    return map[type];
  };

  const onConnectIntegration = (type: ValidIntegrationType) => {
    const status = getIntegrationStatus(type);
    const isIncomplete = status === BrandIntegrationStatus.Incomplete;
    const isTermsRequired = status === BrandIntegrationStatus.TermsRequired;
    const isConnected = status === BrandIntegrationStatus.Connected;

    if (isConnected) return;

    if (type === IntegrationType.Facebook) {
      if (isTermsRequired) {
        onAcceptTermsClick();
        return;
      }

      if (isIncomplete) {
        onConnectAdAccountClick();
        return;
      }

      onMetaConnect();
      return;
    }

    if (type === IntegrationType.Shopify) {
      setShopifyURLRedirect({ path: pathname, key: ShopifyAppRedirect.AIAgent });
      window.open(
        import.meta.env.VITE_SHOPIFY_APP_LISTING_URL || 'https://apps.shopify.com/',
        '_self'
      );
    }

    if (type === IntegrationType.Klaviyo) {
      navigate(`/brand/${brand.brandId}/klaviyo/connect-account?redirect=${pathname}`);
    }
  };

  /* sort integrations, use latest updated as this is the one that was most recently connected */
  const filteredIntegrations = integrations.reduce((map, integration) => {
    const existing = map.get(integration.type);
    const isValid = VALID_INTEGRATION_TYPES.includes(integration.type as ValidIntegrationType);
    if (
      !existing ||
      datefns.isAfter(new Date(integration.lastUpdateAt), new Date(existing.lastUpdateAt))
    ) {
      if (isValid) {
        map.set(integration.type, integration);
      }
    }

    return map;
  }, new Map<string, Integration>());

  const mappedIntegrations = Object.keys(IntegrationMappings).map((type) => {
    const integration = filteredIntegrations.get(type);

    return {
      type: type as ValidIntegrationType,
      name: IntegrationMappings[type as keyof typeof IntegrationMappings],
      isConnected:
        getIntegrationStatus(type as ValidIntegrationType) === BrandIntegrationStatus.Connected,
      lastUpdateAt: integration?.lastUpdateAt || '',
      status: getIntegrationStatus(type as ValidIntegrationType),
      icon: Icons[type as keyof typeof Icons] ?? null,
      onConnect: onConnectIntegration,
    };
  });

  useEffect(() => {
    initializeIntegrations();
  }, []);

  return {
    loading: { api: handleApiLoading, integrations: integrationsLoading },
    messages,
    integrations: mappedIntegrations,
    onSendMessage,
    onFeedbackClick,
  };
};

export default useAIAgent;
